import { Plus, Trash2, Users } from "lucide-react";
import { TitelSelect } from "./TitelSelect";
import { AddressBlock } from "./AddressBlock";
import { DateField } from "./DateField";

/**
 * BETEILIGT_ROLLEN — sortiert nach Häufigkeit im mandatsrelevanten Sachverhalt.
 * "Sonstige" mit Freitext, damit alle Sonderfälle abgedeckt sind.
 */
const BETEILIGT_ROLLEN = [
  "Ehepartner/in",
  "Lebenspartner/in",
  "Kind",
  "Elternteil",
  "Geschwister",
  "Erbe/Miterbe",
  "Bevollmächtigte/r",
  "Betreuer/in",
  "Zeuge/in",
  "Sachverständige/r",
  "Nachbar/in",
  "Arbeitgeber/in",
  "Arbeitnehmer/in",
  "Mitgesellschafter/in",
  "Vermieter/in",
  "Mieter/in",
  "Geschädigte/r",
  "Verursacher/in",
  "Sonstige",
];

const empty = () => ({
  rolle: "",
  rolle_freitext: "",
  anrede: "",
  titel: "",
  vorname: "",
  name: "",
  geburtsdatum: "",
  strasse: "",
  hausnummer: "",
  plz: "",
  ort: "",
  country_code: "DE",
  email: "",
  mobiltelefon: "",
  festnetz: "",
  fax: "",
  bemerkung: "",
});

const TelField = ({ id, label, value, onChange }) => (
  <label className="block">
    <span className="eyebrow text-[#505a74]">{label}</span>
    <input
      type="tel"
      inputMode="tel"
      pattern="[0-9+\s\-()/]*"
      data-testid={id}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
    />
  </label>
);

const TxtField = ({ id, label, value, onChange, className = "", placeholder = "" }) => (
  <label className={`block ${className}`}>
    <span className="eyebrow text-[#505a74]">{label}</span>
    <input
      type="text"
      data-testid={id}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
    />
  </label>
);

export const BeteiligteBlock = ({ value = [], onChange }) => {
  const setAt = (idx, patch) => {
    const next = value.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const add = () => onChange([...(value || []), empty()]);
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4" data-testid="beteiligte-block">
      <p className="text-[13px] text-[#505a74] leading-relaxed">
        Falls weitere Personen unmittelbar am Sachverhalt beteiligt sind
        (z. B. Ehepartner/in, Kinder, Zeugen, Nachbarn, Sachverständige …), können Sie
        diese hier vollständig erfassen. Optional — nur wenn für die Sache relevant.
      </p>

      {(value || []).map((row, idx) => (
        <div
          key={idx}
          data-testid={`beteiligt-row-${idx}`}
          className="border border-[#e8e6df] rounded-sm p-4 md:p-5 bg-[#fafaf7]"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-[11px] font-mono tracking-widest text-[#d90429] uppercase">
              Person {String(idx + 1).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={() => remove(idx)}
              data-testid={`beteiligt-remove-${idx}`}
              className="text-[12px] font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#e8e6df] text-[#505a74] hover:border-[#d90429] hover:text-[#d90429] transition"
            >
              <Trash2 className="w-3.5 h-3.5" /> Entfernen
            </button>
          </div>

          {/* Rolle + Anrede */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="eyebrow text-[#505a74]">Rolle / Bezug *</span>
              <div className="relative mt-2">
                <select
                  data-testid={`beteiligt-${idx}-rolle`}
                  value={row.rolle || ""}
                  onChange={(e) => setAt(idx, { rolle: e.target.value })}
                  className="block w-full appearance-none bg-white border border-[#e8e6df] rounded-sm px-4 py-3 pr-10 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
                >
                  <option value="">— bitte wählen —</option>
                  {BETEILIGT_ROLLEN.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#505a74]">▾</span>
              </div>
            </label>
            {row.rolle === "Sonstige" ? (
              <TxtField
                id={`beteiligt-${idx}-rolle-freitext`}
                label="Rolle näher beschreiben"
                value={row.rolle_freitext}
                onChange={(v) => setAt(idx, { rolle_freitext: v })}
                placeholder="z. B. Nachlasspfleger, Insolvenzverwalter …"
              />
            ) : (
              <TxtField
                id={`beteiligt-${idx}-anrede`}
                label="Anrede"
                value={row.anrede}
                onChange={(v) => setAt(idx, { anrede: v })}
                placeholder="Herr / Frau / Divers"
              />
            )}
          </div>

          {/* Name */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <TitelSelect
              id={`beteiligt-${idx}-titel`}
              value={row.titel}
              onChange={(v) => setAt(idx, { titel: v })}
            />
            <DateField
              id={`beteiligt-${idx}-geburtsdatum`}
              label="Geburtsdatum"
              value={row.geburtsdatum}
              onChange={(v) => setAt(idx, { geburtsdatum: v })}
            />
            <TxtField
              id={`beteiligt-${idx}-vorname`}
              label="Vorname"
              value={row.vorname}
              onChange={(v) => setAt(idx, { vorname: v })}
            />
            <TxtField
              id={`beteiligt-${idx}-name`}
              label="Nachname"
              value={row.name}
              onChange={(v) => setAt(idx, { name: v })}
            />
          </div>

          {/* Anschrift */}
          <div className="mb-4">
            <AddressBlock
              value={{
                strasse: row.strasse,
                hausnummer: row.hausnummer,
                plz: row.plz,
                ort: row.ort,
                country_code: row.country_code || "DE",
              }}
              onChange={(next) => setAt(idx, next)}
              idPrefix={`beteiligt-${idx}`}
            />
          </div>

          {/* Kommunikation */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <TelField
              id={`beteiligt-${idx}-mobil`}
              label="Mobiltelefon"
              value={row.mobiltelefon}
              onChange={(v) => setAt(idx, { mobiltelefon: v })}
            />
            <TelField
              id={`beteiligt-${idx}-festnetz`}
              label="Festnetz"
              value={row.festnetz}
              onChange={(v) => setAt(idx, { festnetz: v })}
            />
            <TelField
              id={`beteiligt-${idx}-fax`}
              label="Fax"
              value={row.fax}
              onChange={(v) => setAt(idx, { fax: v })}
            />
            <TxtField
              id={`beteiligt-${idx}-email`}
              label="E-Mail"
              value={row.email}
              onChange={(v) => setAt(idx, { email: v })}
            />
          </div>

          <TxtField
            id={`beteiligt-${idx}-bemerkung`}
            label="Kurzbemerkung (optional)"
            value={row.bemerkung}
            onChange={(v) => setAt(idx, { bemerkung: v })}
            placeholder="z. B. Zeuge des Vorfalls, gemeinsame Sorge, …"
          />
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        data-testid="beteiligt-add"
        className="btn-ghost px-5 py-2.5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
      >
        <Users className="w-4 h-4" />
        <Plus className="w-3.5 h-3.5" />
        Weitere Person hinzufügen
      </button>
    </div>
  );
};
